import { components } from "@/api-schema";
import { AppRoleAttributeType } from "@/api-schema-enums";
import { mapAttributeValueToName } from "@/common/helpers/map-attribute-value-to-name";
import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

const AVAILABLE_ATTRIBUTE_TYPES: AppRoleAttributeType[] = [
  AppRoleAttributeType.Branch,
  AppRoleAttributeType.Company,
  AppRoleAttributeType.CompanyGroup,
];

interface FormSchemaType {
  applicationUuid: string;
  attributeType: AppRoleAttributeType | undefined;
}

interface AppRoleFormFieldsProps {
  form: UseFormReturn<FormSchemaType>;
  data: AppRoleAttribute[];
}

export function AttributeFormFields({ form, data }: AppRoleFormFieldsProps) {
  const { t } = useTranslation(["ram", "common"]);
  const [open, setOpen] = useState(false);

  const filteredAttributeTypes = useMemo(() => {
    return AVAILABLE_ATTRIBUTE_TYPES.filter((attributeType) => {
      return !data.some((item) => item.type === attributeType);
    });
  }, [data]);

  const value = form.watch("attributeType");
  return (
    <FormField
      control={form.control}
      name="attributeType"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t("ram:Attribute")}</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger className="w-full" asChild>
              <Button
                variant={ButtonVariant.Input}
                colorVariant={ButtonColorVariant.Secondary}
                role="combobox"
                className="w-full justify-between"
                aria-expanded={open}
                aria-label={open ? t("common:Collapse") : t("common:Expand")}
              >
                {value
                  ? mapAttributeValueToName(
                      filteredAttributeTypes.find((attributeType) => attributeType === value) || "unknown",
                    )
                  : t("ram:SelectAttribute")}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder={t("ram:Search")} />
                <CommandList>
                  <CommandEmpty>{t("ram:NoAttributeFound")}</CommandEmpty>
                  <CommandGroup>
                    {filteredAttributeTypes.map((attributeType) => (
                      <CommandItem
                        key={attributeType}
                        value={attributeType}
                        onSelect={(currentValue) => {
                          field.onChange(currentValue);
                          setOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", value === attributeType ? "opacity-100" : "opacity-0")} />
                        {mapAttributeValueToName(attributeType)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormDescription>{t("ram:SelectTheAttributeYouWantToAdd")}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
